/**
 * AgentWorkspace - conversational AI KYC analyst ("Megan"), streaming.
 *
 * Talk to Megan in natural language. She lists/searches applicants and runs
 * first-level KYC reviews on command via tenant-scoped tools. The chat streams
 * her tool activity live ("Searching… → Found 10", "Reviewing Aria — LOW · approved")
 * and the answer text, with markdown rendering. Recommend-only on medium/high;
 * auto-approves low-risk.
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Bot, Sparkles, Send, Loader2, User, Check, AlertTriangle, Plus,
  MessageSquare, Trash2,
} from 'lucide-react';
import { Button, Badge } from './ui';
import { AIService } from '../services';
import { useAuth } from '../auth';
import { MarkdownLite } from './shared/MarkdownLite';

const AGENT_NAME = 'Megan';

const SUGGESTIONS = [
  'Show me the last 10 applicants',
  'Review the most recent pending applicants',
  'Review Aria Bennett',
  'Who still needs review?',
];

// Remember which thread the user last had open, so a reload reopens it
// (rather than always jumping to the most-recent thread).
function activeKey(userSub) {
  return `trustgate-megan-active-${userSub || 'anon'}`;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ToolChip({ event }) {
  const { status, summary } = event;
  return (
    <div
      className={[
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs max-w-full',
        status === 'pending' && 'bg-teal-500/10 border-teal-500/30 text-teal-600 dark:text-teal-400 animate-pulse',
        status === 'complete' && 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400',
        status === 'error' && 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
      ].filter(Boolean).join(' ')}
    >
      {status === 'pending' && <Loader2 size={12} className="shrink-0 animate-spin" />}
      {status === 'complete' && <Check size={12} className="shrink-0" />}
      {status === 'error' && <AlertTriangle size={12} className="shrink-0" />}
      <span className="truncate">{summary}</span>
    </div>
  );
}

function AssistantBubble({ msg }) {
  const events = msg.toolEvents || [];
  const showThinking = msg.streaming && !msg.content && events.length === 0;
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-teal-500/15">
        <Bot size={16} className="text-teal-500" />
      </div>
      <div className="max-w-[80%] space-y-2 min-w-0">
        {events.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {events.map((e) => (
              <ToolChip key={e.id} event={e} />
            ))}
          </div>
        )}
        {showThinking && (
          <div className="flex items-center gap-2 text-xs text-grey-400 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" /> Thinking…
          </div>
        )}
        {msg.content && (
          <div className="rounded-xl px-3.5 py-2.5 text-sm bg-grey-100 dark:bg-grey-800 text-grey-900 dark:text-grey-50">
            <MarkdownLite text={msg.content} />
            {msg.streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-teal-500 animate-pulse" />}
          </div>
        )}
      </div>
    </div>
  );
}

function UserBubble({ content }) {
  return (
    <div className="flex gap-3 flex-row-reverse">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-grey-200 dark:bg-grey-700">
        <User size={16} className="text-grey-600 dark:text-grey-300" />
      </div>
      <div className="max-w-[78%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed bg-teal-500 text-white">
        {content}
      </div>
    </div>
  );
}

function ThreadSidebar({ threads, activeThreadId, onSelect, onNew, onDelete, busy }) {
  return (
    <div className="w-60 shrink-0 flex flex-col border-r border-grey-200 dark:border-grey-800 pr-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={onNew}
        disabled={busy}
        className="w-full justify-center gap-1.5 mb-2 shrink-0"
      >
        <Plus size={14} /> New chat
      </Button>
      <div className="flex-1 overflow-y-auto space-y-0.5 pr-0.5">
        {threads.length === 0 && (
          <div className="text-[11px] text-grey-400 px-2 py-3 text-center">
            No conversations yet
          </div>
        )}
        {threads.map((t) => {
          const active = t.id === activeThreadId;
          return (
            <div
              key={t.id}
              onClick={() => !busy && onSelect(t.id)}
              className={[
                'group relative flex items-center gap-2 rounded-lg px-2 py-2 cursor-pointer text-sm transition-colors',
                active
                  ? 'bg-teal-500/10 text-teal-700 dark:text-teal-300'
                  : 'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
              ].join(' ')}
            >
              <MessageSquare size={14} className="shrink-0 opacity-60" />
              <div className="min-w-0 flex-1">
                <div className="truncate leading-tight">{t.title || 'New chat'}</div>
                <div className="text-[10px] text-grey-400 leading-tight">
                  {relativeTime(t.last_message_at)}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(t.id);
                }}
                disabled={busy}
                title="Delete conversation"
                className="opacity-0 group-hover:opacity-100 shrink-0 p-1 rounded text-grey-400 hover:text-red-500 transition-opacity"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AgentWorkspace() {
  const { getToken, user } = useAuth();
  const userSub = user?.sub || null;
  const service = useMemo(() => new AIService(getToken), [getToken]);
  const [messages, setMessages] = useState([]); // {role, content, toolEvents?, streaming?}
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);
  const activeThreadRef = useRef(null);
  activeThreadRef.current = activeThreadId; // always-current id for the stream callback

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const refreshThreads = useCallback(async () => {
    try {
      const data = await service.getThreads();
      if (Array.isArray(data?.threads)) setThreads(data.threads);
      return data?.threads || [];
    } catch {
      return [];
    }
  }, [service]);

  const openThread = useCallback(
    async (threadId) => {
      if (!threadId) return;
      try {
        const data = await service.getThread(threadId);
        const restored = (data?.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          toolEvents: m.toolEvents || [],
        }));
        setMessages(restored);
        setActiveThreadId(threadId);
        try { localStorage.setItem(activeKey(userSub), threadId); } catch { /* ignore */ }
      } catch {
        /* thread gone — leave current view */
      }
    },
    [service, userSub]
  );

  // On mount: load the sidebar list + reopen the last-active thread (or the
  // most-recent one). Server is authoritative.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await refreshThreads();
      if (cancelled) return;
      let want = null;
      try { want = localStorage.getItem(activeKey(userSub)); } catch { /* ignore */ }
      const exists = want && list.some((t) => t.id === want);
      if (exists) {
        await openThread(want);
      } else {
        // Fall back to the most-recent thread via the history convenience route.
        try {
          const hist = await service.getChatHistory();
          if (!cancelled && hist?.thread_id) {
            setActiveThreadId(hist.thread_id);
            setMessages(
              (hist.messages || []).map((m) => ({
                role: m.role,
                content: m.content,
                toolEvents: m.toolEvents || [],
              }))
            );
            try { localStorage.setItem(activeKey(userSub), hist.thread_id); } catch { /* ignore */ }
          }
        } catch {
          /* no history yet */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSub]);

  const newChat = useCallback(() => {
    if (busy) return;
    setMessages([]);
    setActiveThreadId(null);
    try { localStorage.removeItem(activeKey(userSub)); } catch { /* ignore */ }
  }, [busy, userSub]);

  const deleteThread = useCallback(
    async (threadId) => {
      if (busy) return;
      try {
        await service.deleteThread(threadId);
      } catch {
        /* best-effort */
      }
      if (threadId === activeThreadRef.current) {
        setMessages([]);
        setActiveThreadId(null);
        try { localStorage.removeItem(activeKey(userSub)); } catch { /* ignore */ }
      }
      refreshThreads();
    },
    [busy, service, userSub, refreshThreads]
  );

  const updateLastAssistant = (fn) => {
    setMessages((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant') {
          next[i] = fn({ ...next[i] });
          break;
        }
      }
      return next;
    });
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    setBusy(true);

    const history = [...messages, { role: 'user', content }];
    // history for the API: only role + content
    const apiHistory = history.map((m) => ({ role: m.role, content: m.content }));
    setMessages([
      ...history,
      { role: 'assistant', content: '', toolEvents: [], streaming: true },
    ]);

    try {
      await service.streamChat(apiHistory, {
        threadId: activeThreadRef.current,
        onEvent: (ev) => {
          if (ev.type === 'thread') {
            // Backend resolved/created the thread for this turn. Track it so
            // subsequent messages land in the same thread.
            if (ev.id) {
              setActiveThreadId(ev.id);
              activeThreadRef.current = ev.id;
              try { localStorage.setItem(activeKey(userSub), ev.id); } catch { /* ignore */ }
            }
          } else if (ev.type === 'tool') {
            updateLastAssistant((m) => {
              const events = [...(m.toolEvents || [])];
              const idx = events.findIndex((e) => e.id === ev.id);
              const entry = { id: ev.id, summary: ev.summary, status: ev.status };
              if (idx >= 0) events[idx] = entry;
              else events.push(entry);
              return { ...m, toolEvents: events };
            });
          } else if (ev.type === 'text') {
            updateLastAssistant((m) => ({ ...m, content: (m.content || '') + (ev.delta || '') }));
          } else if (ev.type === 'error') {
            updateLastAssistant((m) => ({
              ...m,
              content: (m.content || '') + `\n\n⚠ ${ev.message || 'Something went wrong.'}`,
              streaming: false,
            }));
          } else if (ev.type === 'done') {
            updateLastAssistant((m) => ({ ...m, streaming: false }));
          }
        },
      });
    } catch (e) {
      updateLastAssistant((m) => ({
        ...m,
        content: (m.content || '') + `\n\n⚠ ${e?.message || 'Request failed.'}`,
        streaming: false,
      }));
    } finally {
      updateLastAssistant((m) => ({ ...m, streaming: false }));
      setBusy(false);
      refreshThreads(); // pick up a new thread / updated title + ordering
    }
  };

  return (
    <div className="max-w-[1140px] mx-auto px-2 py-2 flex gap-3 h-[calc(100vh-130px)]">
      <ThreadSidebar
        threads={threads}
        activeThreadId={activeThreadId}
        onSelect={openThread}
        onNew={newChat}
        onDelete={deleteThread}
        busy={busy}
      />

      <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 shrink-0">
        <div className="w-11 h-11 rounded-xl bg-teal-500/15 flex items-center justify-center shrink-0">
          <Bot size={22} className="text-teal-500" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-semibold tracking-tight text-grey-900 dark:text-grey-50">
              {AGENT_NAME}
            </h1>
            <Badge variant="info" className="gap-1">
              <Sparkles size={11} /> AI KYC Analyst
            </Badge>
          </div>
          <p className="text-xs text-grey-500 dark:text-grey-400 mt-0.5">
            Ask me to review applicants. I check ID + screening, score risk, write a note,
            auto-approve low-risk, and escalate the rest.
          </p>
        </div>
      </div>

      {/* Thread */}
      <div ref={threadRef} className="flex-1 overflow-y-auto space-y-4 pr-1 pb-2">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-teal-500/10 flex items-center justify-center">
              <Bot size={28} className="text-teal-500" />
            </div>
            <div className="text-grey-500 dark:text-grey-400 text-sm max-w-sm">
              Hi, I'm {AGENT_NAME}. Tell me what to do — review one applicant, a few, or the
              latest batch. Try one of these:
            </div>
            <div className="flex flex-wrap gap-2 justify-center max-w-md">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-grey-200 dark:border-grey-700 text-grey-700 dark:text-grey-300 hover:border-teal-500 hover:text-teal-500 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === 'user' ? (
            <UserBubble key={i} content={m.content} />
          ) : (
            <AssistantBubble key={i} msg={m} />
          )
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 pt-2 border-t border-grey-200 dark:border-grey-800">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={`Message ${AGENT_NAME}…  (e.g. "review the last 5 applicants")`}
            className="flex-1 resize-none max-h-32 px-3.5 py-2.5 rounded-xl border border-grey-200 dark:border-grey-700 bg-white dark:bg-grey-900 text-grey-900 dark:text-grey-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
          />
          <Button size="default" onClick={() => send()} disabled={busy || !input.trim()} className="h-[42px]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
        <div className="text-[11px] text-grey-400 mt-1.5 px-1">
          {AGENT_NAME} takes real action (reviews, notes, low-risk approvals) — all recorded to the audit log.
        </div>
      </div>
      </div>
    </div>
  );
}
